import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { border } from '@arcade/web'

import arcadeColors from '../../theme/arcadeColors'
import { Flex } from '../layout/Flex'
import { Position } from '../Position/Position'
import { Avatar, AvatarProps, avatarSizeDimensions, DISABLED_OPACITY, Size } from './ArcadeAvatar'

interface StackedAvatarProps {
  stackedAvatars: AvatarProps[]
  size: Size
  disabled?: boolean
}

const DOULBE_AVATAR_SIZING: Record<string, Size> = {
  small: 'xsmall',
  medium: 'small',
  large: 'medium',
}

const getStackedAvatarProperties = (size: Size) => {
  return {
    borderColor: arcadeColors.borderApp,
    borderWidth: border.width.medium,
    /**
     * disabled prop is controlled by the StackedAvatarWrapper.
     * Otherwise, the children avatars will have an opacity overlay bug.
     */
    disabled: false,
    size: size,
  }
}

const StackedAvatarWrapper = styled(Flex)<{ width: number; height: number; disabled?: boolean }>`
  position: relative;
  overflow: hidden;
  ${({ disabled }) => disabled && `opacity: ${DISABLED_OPACITY};`};
  ${({ width, height }) => {
    return css`
      height: ${height}px;
      width: ${width}px;
    `
  }}
`

const DoubleAvatar = ({ stackedAvatars, size, disabled }: StackedAvatarProps) => {
  if (size === 'xsmall') {
    // gracefully handle xsmall size; xsmall used for mobile web recent activity avatars
    return <Avatar {...stackedAvatars[stackedAvatars.length - 1]} size="xsmall" disabled={disabled} />
  }

  const backgroundAvatar = stackedAvatars[0]
  const foregroundAvatar = stackedAvatars[1]

  const doubleStackedSize = DOULBE_AVATAR_SIZING[size]
  const wrapperDimension = avatarSizeDimensions[size].width

  const stackedAvatarProperties = getStackedAvatarProperties(doubleStackedSize)

  return (
    <StackedAvatarWrapper
      width={wrapperDimension}
      height={wrapperDimension}
      disabled={disabled}
      data-testid="double-stacked-avatar-wrapper"
    >
      <Avatar {...foregroundAvatar} size={doubleStackedSize} />
      <Position position="absolute" bottom="0px" right="0px">
        <Avatar {...backgroundAvatar} {...stackedAvatarProperties} />
      </Position>
    </StackedAvatarWrapper>
  )
}

const HorizontalStackedAvatar = ({ stackedAvatars, size, disabled }: StackedAvatarProps) => {
  // We want to render avatars from the last one to the first one
  const firstAvatar = stackedAvatars[stackedAvatars.length - 1]

  // Width is the sum of the width of the first avatar and the width of the other avatars (2/3 overlap)
  const wrapperWidth = avatarSizeDimensions[size].width * (1 + (2 / 3) * (stackedAvatars.length - 1))
  const wrapperHeight = avatarSizeDimensions[size].width

  const stackedAvatarProperties = getStackedAvatarProperties(size)

  return (
    <StackedAvatarWrapper
      width={wrapperWidth}
      height={wrapperHeight}
      disabled={disabled}
      data-testid="stacked-avatar-wrapper"
    >
      <Avatar {...firstAvatar} size={size} />
      {stackedAvatars
        .slice(0, -1)
        .reverse()
        .map((avatar, index) => {
          // Position each avatar at 2/3 intervals to create 1/3 overlap
          const leftOffset = avatarSizeDimensions[size].width * (2 / 3) * (index + 1)
          return (
            <Position position="absolute" top="0" left={`${leftOffset}px`} key={index}>
              <Avatar key={index} {...avatar} {...stackedAvatarProperties} />
            </Position>
          )
        })}
    </StackedAvatarWrapper>
  )
}

/**
 * Used to stack up to 3 avatars on top of each other.
 */
export const StackedAvatar = ({ stackedAvatars, size = 'medium', disabled = false }: StackedAvatarProps) => {
  if (stackedAvatars.length === 2) {
    return <DoubleAvatar stackedAvatars={stackedAvatars} size={size} disabled={disabled} />
  }
  return <HorizontalStackedAvatar stackedAvatars={stackedAvatars} size={size} disabled={disabled} />
}
